import { useQuery } from "convex/react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  CalendarRange,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useMemo, useState } from "react";
import type { ReactNode } from "react";

import { api } from "../../convex/_generated/api";
import type { BusinessMembership, ConversationSummary } from "../inbox/types";

const illustrativeKpiData = [
  { period: "Jan", value: 240 },
  { period: "Mar", value: 285 },
  { period: "May", value: 320 },
  { period: "Jul", value: 390 },
  { period: "Sep", value: 430 },
  { period: "Nov", value: 482.9 },
];

type ComparisonPreset =
  "none" | "yesterday" | "previousYear" | "previousYearMatch" | "custom";

const comparisonOptions: Array<{
  value: ComparisonPreset;
  label: string;
}> = [
  { value: "none", label: "No comparison" },
  { value: "yesterday", label: "Yesterday" },
  { value: "previousYear", label: "Previous year" },
  { value: "previousYearMatch", label: "Previous year (match day)" },
  { value: "custom", label: "Custom" },
];

export function WorkspaceOverview({
  business,
}: {
  business: BusinessMembership;
}) {
  const conversations = useQuery(api.conversations.listForBusiness, {
    businessId: business.businessId,
  }) as ConversationSummary[] | undefined;
  const [comparison, setComparison] = useState<ComparisonPreset>("none");
  const [customComparisonStart, setCustomComparisonStart] = useState("");
  const [customComparisonEnd, setCustomComparisonEnd] = useState("");
  const [openMenu, setOpenMenu] = useState<"comparison" | null>(null);
  const chartData = useMemo(
    () =>
      buildContactGrowthData(
        conversations,
        comparison === "custom" ? customComparisonStart : undefined,
        comparison === "custom" ? customComparisonEnd : undefined,
      ),
    [comparison, conversations, customComparisonEnd, customComparisonStart],
  );
  const liveTotalContacts = new Set(
    (conversations ?? []).map((conversation) => conversation.contactId),
  ).size;
  const totalContacts = liveTotalContacts || 482900;

  return (
    <div aria-label="Workspace overview" className="workspace-overview">
      <div className="workspace-overview-toolbar" aria-label="Report filters">
        <WorkspaceFilter
          ariaLabel="Comparison period"
          icon={<CalendarRange aria-hidden="true" size={19} strokeWidth={2} />}
          label={
            comparison === "custom"
              ? formatDateRangeLabel(customComparisonStart, customComparisonEnd)
              : "Oct 19, 2026"
          }
          menuId="comparison-menu"
          onToggle={() =>
            setOpenMenu((current) =>
              current === "comparison" ? null : "comparison",
            )
          }
          open={openMenu === "comparison"}
        >
          {comparison === "custom" ? (
            <WorkspaceDateRangePicker
              endDate={customComparisonEnd}
              onApply={() => setOpenMenu(null)}
              onBack={() => setComparison("none")}
              onEndDateChange={setCustomComparisonEnd}
              onStartDateChange={setCustomComparisonStart}
              startDate={customComparisonStart}
            />
          ) : null}
          {comparison !== "custom"
            ? comparisonOptions.map((option) => (
                <WorkspaceFilterOption
                  key={option.value}
                  active={comparison === option.value}
                  label={option.label}
                  onClick={() => {
                    setComparison(option.value);
                    if (option.value !== "custom") setOpenMenu(null);
                  }}
                />
              ))
            : null}
        </WorkspaceFilter>
      </div>
      <motion.section
        className="workspace-total-contacts"
        aria-label="Total contacts"
        initial={{ opacity: 0, scale: 0.98, y: 6 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="workspace-total-contacts-header">
          <div className="workspace-total-contacts-heading">
            <h2>Total Contacts</h2>
            <div className="workspace-total-contacts-value">
              <span>{totalContacts.toLocaleString()}</span>
            </div>
          </div>
        </div>
        <div className="workspace-total-contacts-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 8, right: 2, left: 2, bottom: 0 }}
            >
              <defs>
                <linearGradient
                  id="nkuli-total-contacts-gradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor="#2563eb" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                vertical={false}
                stroke="#d8e0dc"
                strokeDasharray="2 4"
              />
              <XAxis
                dataKey="period"
                axisLine={{ stroke: "#b8c7c0" }}
                tickLine={false}
                tick={{
                  fill: "#53655e",
                  fontFamily: "ui-sans-serif, system-ui, sans-serif",
                  fontSize: 11,
                }}
                tickFormatter={formatChartPeriod}
                minTickGap={20}
              />
              <YAxis
                axisLine={{ stroke: "#b8c7c0" }}
                tickLine={false}
                tick={{
                  fill: "#53655e",
                  fontFamily: "ui-sans-serif, system-ui, sans-serif",
                  fontSize: 11,
                }}
                tickCount={4}
                width={32}
              />
              <Tooltip
                cursor={false}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  return (
                    <div className="workspace-total-contacts-tooltip">
                      {payload[0].value} contacts
                    </div>
                  );
                }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#2563eb"
                strokeWidth={2.5}
                strokeLinecap="round"
                fill="url(#nkuli-total-contacts-gradient)"
                isAnimationActive
                animationDuration={700}
                animationEasing="ease-out"
                connectNulls
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </motion.section>
      <WorkspaceInsights chartData={chartData} />
    </div>
  );
}

function WorkspaceInsights({
  chartData,
}: {
  chartData: Array<{ period: string; value: number }>;
}) {
  const dashboardData = buildDashboardData(chartData);

  return (
    <section
      aria-label="Workspace reporting details"
      className="workspace-insights"
    >
      <InsightCard title="New Contacts">
        <ResponsiveContainer height="100%" width="100%">
          <BarChart
            data={dashboardData.newContacts}
            margin={{ top: 8, right: 4, left: 0, bottom: 0 }}
          >
            <CartesianGrid
              vertical={false}
              stroke="#d8e0dc"
              strokeDasharray="2 4"
            />
            <XAxis
              dataKey="period"
              tick={chartTick}
              tickLine={false}
              axisLine={{ stroke: "#b8c7c0" }}
            />
            <YAxis
              tick={chartTick}
              tickLine={false}
              axisLine={{ stroke: "#b8c7c0" }}
              width={28}
            />
            <Tooltip
              cursor={{ fill: "#f4f7f6" }}
              content={<InsightTooltip suffix=" contacts" />}
            />
            <Bar
              dataKey="value"
              fill="#2563eb"
              radius={[8, 8, 0, 0]}
              isAnimationActive
              animationDuration={520}
            />
          </BarChart>
        </ResponsiveContainer>
      </InsightCard>

      <InsightCard title="All Contacts by Channel">
        <ResponsiveContainer height="100%" width="100%">
          <BarChart
            data={dashboardData.channelMix}
            layout="vertical"
            margin={{ top: 4, right: 8, left: 4, bottom: 2 }}
          >
            <CartesianGrid
              horizontal={false}
              stroke="#d8e0dc"
              strokeDasharray="2 4"
            />
            <XAxis
              domain={[0, 100]}
              tick={chartTick}
              tickLine={false}
              axisLine={{ stroke: "#b8c7c0" }}
              unit="%"
            />
            <YAxis
              dataKey="label"
              type="category"
              tick={{ ...chartTick, textAnchor: "end" }}
              tickLine={false}
              axisLine={false}
              width={68}
            />
            <Tooltip
              cursor={{ fill: "#f4f7f6" }}
              content={<InsightTooltip suffix="%" />}
            />
            <Bar
              dataKey="value"
              fill="#2563eb"
              radius={[0, 8, 8, 0]}
              isAnimationActive
              animationDuration={560}
            />
          </BarChart>
        </ResponsiveContainer>
      </InsightCard>

      <InsightCard title="New Contacts by Channel">
        <ResponsiveContainer height="100%" width="100%">
          <BarChart
            data={dashboardData.channelGrowth}
            margin={{ top: 8, right: 4, left: 0, bottom: 0 }}
          >
            <CartesianGrid
              vertical={false}
              stroke="#d8e0dc"
              strokeDasharray="2 4"
            />
            <XAxis
              dataKey="period"
              tick={chartTick}
              tickLine={false}
              axisLine={{ stroke: "#b8c7c0" }}
            />
            <YAxis
              tick={chartTick}
              tickLine={false}
              axisLine={{ stroke: "#b8c7c0" }}
              width={28}
            />
            <Tooltip
              cursor={{ fill: "#f4f7f6" }}
              content={<InsightTooltip suffix=" contacts" />}
            />
            <Bar
              dataKey="whatsapp"
              stackId="channel"
              fill="#2563eb"
              radius={[8, 8, 0, 0]}
              isAnimationActive
              animationDuration={520}
            />
            <Bar
              dataKey="instagram"
              stackId="channel"
              fill="#9db8f5"
              radius={[8, 8, 0, 0]}
              isAnimationActive
              animationDuration={520}
            />
          </BarChart>
        </ResponsiveContainer>
      </InsightCard>

      <InsightCard title="New Contacts by Source">
        <ResponsiveContainer height="100%" width="100%">
          <BarChart
            data={dashboardData.sourceMix}
            layout="vertical"
            margin={{ top: 4, right: 8, left: 4, bottom: 2 }}
          >
            <CartesianGrid
              horizontal={false}
              stroke="#d8e0dc"
              strokeDasharray="2 4"
            />
            <XAxis
              tick={chartTick}
              tickLine={false}
              axisLine={{ stroke: "#b8c7c0" }}
            />
            <YAxis
              dataKey="label"
              type="category"
              tick={{ ...chartTick, textAnchor: "end" }}
              tickLine={false}
              axisLine={false}
              width={68}
            />
            <Tooltip
              cursor={{ fill: "#f4f7f6" }}
              content={<InsightTooltip suffix=" contacts" />}
            />
            <Bar
              dataKey="value"
              fill="#6b8fdf"
              radius={[0, 8, 8, 0]}
              isAnimationActive
              animationDuration={560}
            />
          </BarChart>
        </ResponsiveContainer>
      </InsightCard>

      <InsightCard title="New Contacts by Language">
        <ResponsiveContainer height="100%" width="100%">
          <BarChart
            data={dashboardData.languageMix}
            margin={{ top: 8, right: 4, left: 0, bottom: 0 }}
          >
            <CartesianGrid
              vertical={false}
              stroke="#d8e0dc"
              strokeDasharray="2 4"
            />
            <XAxis
              dataKey="label"
              tick={chartTick}
              tickLine={false}
              axisLine={{ stroke: "#b8c7c0" }}
            />
            <YAxis
              tick={chartTick}
              tickLine={false}
              axisLine={{ stroke: "#b8c7c0" }}
              width={28}
            />
            <Tooltip
              cursor={{ fill: "#f4f7f6" }}
              content={<InsightTooltip suffix=" contacts" />}
            />
            <Bar
              dataKey="value"
              fill="#2563eb"
              radius={[8, 8, 0, 0]}
              isAnimationActive
              animationDuration={520}
            />
          </BarChart>
        </ResponsiveContainer>
      </InsightCard>

      <InsightCard title="Blocked Contacts">
        <ResponsiveContainer height="100%" width="100%">
          <LineChart
            data={dashboardData.blockedContacts}
            margin={{ top: 8, right: 4, left: 0, bottom: 0 }}
          >
            <CartesianGrid
              vertical={false}
              stroke="#d8e0dc"
              strokeDasharray="2 4"
            />
            <XAxis
              dataKey="period"
              tick={chartTick}
              tickLine={false}
              axisLine={{ stroke: "#b8c7c0" }}
            />
            <YAxis
              tick={chartTick}
              tickLine={false}
              axisLine={{ stroke: "#b8c7c0" }}
              width={28}
            />
            <Tooltip
              cursor={{ stroke: "#b8c7c0", strokeDasharray: "2 4" }}
              content={<InsightTooltip suffix=" blocked" />}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#2563eb"
              strokeWidth={2.5}
              strokeLinecap="round"
              dot={{ fill: "#2563eb", r: 3 }}
              activeDot={{ r: 5 }}
              isAnimationActive
              animationDuration={620}
            />
          </LineChart>
        </ResponsiveContainer>
      </InsightCard>

      <InsightCard
        className="workspace-insight-card--table"
        title="Recent Contacts"
      >
        <div className="workspace-recent-contacts-table-wrap">
          <table className="workspace-recent-contacts-table">
            <thead>
              <tr>
                <th scope="col">Contact</th>
                <th scope="col">Channel</th>
                <th scope="col">Source</th>
                <th scope="col">Period</th>
              </tr>
            </thead>
            <tbody>
              {dashboardData.recentContacts.map((contact) => (
                <tr key={contact.name}>
                  <td>{contact.name}</td>
                  <td>{contact.channel}</td>
                  <td>{contact.source}</td>
                  <td>{contact.period}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </InsightCard>
    </section>
  );
}

function InsightCard({
  children,
  className = "",
  title,
}: {
  children: ReactNode;
  className?: string;
  title: string;
}) {
  return (
    <article className={`workspace-insight-card ${className}`.trim()}>
      <header className="workspace-insight-card-header">
        <div>
          <h3>{title}</h3>
        </div>
      </header>
      <div className="workspace-insight-card-chart">{children}</div>
    </article>
  );
}

function InsightTooltip({
  active,
  payload,
  suffix = "",
}: {
  active?: boolean;
  payload?: Array<{ value?: number | string }>;
  suffix?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="workspace-total-contacts-tooltip">
      {payload[0].value}
      {suffix}
    </div>
  );
}

const chartTick = {
  fill: "#53655e",
  fontFamily: "var(--nkuli-ui-font)",
  fontSize: 10,
};

function buildDashboardData(
  chartData: Array<{ period: string; value: number }>,
) {
  const periods = chartData.map((point) => point.period);
  const lastValue = chartData.at(-1)?.value ?? 482.9;
  const scale = Math.max(lastValue / 482.9, 0.35);
  const newContacts = chartData.map((point, index) => ({
    period: point.period,
    value: Math.max(
      Math.round(
        (point.value - (chartData[index - 1]?.value ?? point.value)) * 1.4,
      ),
      8,
    ),
  }));
  const labels = periods.map(formatChartPeriod);

  return {
    newContacts,
    channelMix: [
      { label: "WhatsApp", value: Math.round(68 * scale) },
      { label: "Instagram", value: Math.round(44 * scale) },
      { label: "Messenger", value: Math.round(29 * scale) },
      { label: "Web Chat", value: Math.round(18 * scale) },
    ],
    channelGrowth: newContacts.map((point, index) => ({
      period: point.period,
      whatsapp: Math.round(point.value * 0.62),
      instagram: Math.round(point.value * 0.38),
      label: labels[index],
    })),
    sourceMix: [
      { label: "Organic", value: Math.round(72 * scale) },
      { label: "QR code", value: Math.round(48 * scale) },
      { label: "Website", value: Math.round(36 * scale) },
      { label: "Referral", value: Math.round(24 * scale) },
    ],
    languageMix: [
      { label: "English", value: Math.round(82 * scale) },
      { label: "isiZulu", value: Math.round(64 * scale) },
      { label: "Sesotho", value: Math.round(42 * scale) },
      { label: "Other", value: Math.round(28 * scale) },
    ],
    blockedContacts: periods.map((period, index) => ({
      period,
      value: Math.round((12 + index * 2.4 + (index % 2 ? 2 : 0)) * scale),
    })),
    recentContacts: [
      {
        name: "Contact A",
        channel: "WhatsApp",
        source: "QR code",
        period: labels.at(-1) ?? "Today",
      },
      {
        name: "Contact B",
        channel: "Instagram",
        source: "Organic",
        period: labels.at(-2) ?? "Yesterday",
      },
      {
        name: "Contact C",
        channel: "Web Chat",
        source: "Website",
        period: labels.at(-3) ?? "Earlier",
      },
    ],
  };
}

function WorkspaceFilter({
  ariaLabel,
  children,
  icon,
  label,
  menuId,
  onToggle,
  open,
}: {
  ariaLabel: string;
  children: ReactNode;
  icon: ReactNode;
  label: string;
  menuId: string;
  onToggle: () => void;
  open: boolean;
}) {
  return (
    <div className="workspace-filter">
      <button
        aria-controls={menuId}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={ariaLabel}
        className="workspace-filter-trigger"
        onClick={onToggle}
        type="button"
      >
        {icon}
        <span>{label}</span>
        <ChevronDown
          aria-hidden="true"
          className={open ? "workspace-filter-chevron--open" : undefined}
          size={17}
          strokeWidth={2}
        />
      </button>
      {open ? (
        <div className="workspace-filter-menu" id={menuId} role="menu">
          {children}
        </div>
      ) : null}
    </div>
  );
}

function WorkspaceFilterOption({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      aria-checked={active}
      className={`workspace-filter-option${active ? " workspace-filter-option--active" : ""}`}
      onClick={onClick}
      role="menuitemradio"
      type="button"
    >
      {label}
    </button>
  );
}

function WorkspaceDateRangePicker({
  endDate,
  onApply,
  onBack,
  onEndDateChange,
  onStartDateChange,
  startDate,
}: {
  endDate: string;
  onApply: () => void;
  onBack: () => void;
  onEndDateChange: (value: string) => void;
  onStartDateChange: (value: string) => void;
  startDate: string;
}) {
  const [visibleMonth, setVisibleMonth] = useState(() => new Date(2026, 9, 1));
  const calendarDays = getCalendarDays(visibleMonth);

  function selectDay(value: string) {
    if (!startDate || (startDate && endDate)) {
      onStartDateChange(value);
      onEndDateChange("");
      return;
    }

    if (value < startDate) {
      onStartDateChange(value);
      onEndDateChange(startDate);
      return;
    }

    onEndDateChange(value);
  }

  return (
    <div aria-label="Custom date range" className="workspace-date-range-picker">
      <button
        aria-label="Back to comparison options"
        className="workspace-date-range-back"
        onClick={onBack}
        type="button"
      >
        <ArrowLeft aria-hidden="true" size={18} />
        <span>Back</span>
      </button>
      <div className="workspace-date-range-fields">
        <div className="workspace-date-range-field">
          <span>From</span>
          <strong>
            {startDate ? formatDateLabel(startDate) : "Select date"}
          </strong>
        </div>
        <ArrowRight aria-hidden="true" size={18} />
        <div className="workspace-date-range-field">
          <span>To</span>
          <strong>{endDate ? formatDateLabel(endDate) : "Select date"}</strong>
        </div>
        <Clock3 aria-hidden="true" size={20} />
      </div>
      <div className="workspace-calendar-header">
        <button
          aria-label="Previous month"
          className="workspace-calendar-nav"
          onClick={() => setVisibleMonth(addMonths(visibleMonth, -1))}
          type="button"
        >
          <ChevronLeft aria-hidden="true" size={20} />
        </button>
        <strong>
          {new Intl.DateTimeFormat("en", {
            month: "long",
            year: "numeric",
          }).format(visibleMonth)}
        </strong>
        <button
          aria-label="Next month"
          className="workspace-calendar-nav"
          onClick={() => setVisibleMonth(addMonths(visibleMonth, 1))}
          type="button"
        >
          <ChevronRight aria-hidden="true" size={20} />
        </button>
      </div>
      <div className="workspace-calendar-weekdays" aria-hidden="true">
        {weekdayLabels.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
      <div
        aria-label="Custom date calendar"
        className="workspace-calendar-grid"
        role="grid"
      >
        {calendarDays.map((day) => {
          const selected = day.value === startDate || day.value === endDate;
          const inRange = Boolean(
            startDate &&
            endDate &&
            day.value > startDate &&
            day.value < endDate,
          );

          return day.value ? (
            <button
              aria-label={formatDateLabel(day.value)}
              aria-selected={selected}
              className={`workspace-calendar-day${selected ? " workspace-calendar-day--selected" : ""}${inRange ? " workspace-calendar-day--range" : ""}`}
              key={day.key}
              onClick={() => selectDay(day.value)}
              role="gridcell"
              type="button"
            >
              {day.day}
            </button>
          ) : (
            <span
              aria-hidden="true"
              className="workspace-calendar-day--empty"
              key={day.key}
            />
          );
        })}
      </div>
      <div className="workspace-date-range-actions">
        <button
          className="workspace-date-range-cancel"
          onClick={onBack}
          type="button"
        >
          Cancel
        </button>
        <button
          className="workspace-date-range-apply"
          disabled={!startDate || !endDate}
          onClick={onApply}
          type="button"
        >
          Apply
        </button>
      </div>
    </div>
  );
}

const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function getCalendarDays(month: Date) {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const firstDay = new Date(year, monthIndex, 1).getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const days = Array.from({ length: firstDay }, (_, index) => ({
    day: 0,
    key: `empty-${index}`,
    value: "",
  }));

  for (let day = 1; day <= daysInMonth; day += 1) {
    const value = toDateInputValue(new Date(year, monthIndex, day));
    days.push({ day, key: value, value });
  }

  return days;
}

function addMonths(date: Date, amount: number) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

function toDateInputValue(date: Date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function formatDateLabel(value: string) {
  if (!value) return "Select date";

  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(`${value}T00:00:00`));
}

function formatDateRangeLabel(startDate: string, endDate: string) {
  if (!startDate || !endDate) return "Custom";

  return `${formatDateLabel(startDate)} – ${formatDateLabel(endDate)}`;
}

function formatChartPeriod(value: string | number) {
  if (typeof value === "number") return String(value);
  if (!value.includes("T")) return value;

  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
  }).format(new Date(value));
}

function buildContactGrowthData(
  conversations: ConversationSummary[] | undefined,
  startDate?: string,
  endDate?: string,
) {
  const defaultCutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const startTimestamp = startDate
    ? new Date(`${startDate}T00:00:00`).getTime()
    : defaultCutoff;
  const endTimestamp = endDate
    ? new Date(`${endDate}T23:59:59.999`).getTime()
    : Date.now();
  const seen = new Set<string>();

  const liveData = (conversations ?? [])
    .filter(
      (conversation) =>
        conversation.createdAt >= startTimestamp &&
        conversation.createdAt <= endTimestamp,
    )
    .sort((left, right) => left.createdAt - right.createdAt)
    .map((conversation) => {
      seen.add(conversation.contactId);
      return {
        period: new Date(conversation.createdAt).toISOString(),
        value: seen.size,
      };
    });

  return liveData.length > 0
    ? liveData
    : getIllustrativeData(startDate, endDate);
}

function getIllustrativeData(startDate?: string, endDate?: string) {
  if (!startDate || !endDate) return illustrativeKpiData;

  const start = new Date(`${startDate}T00:00:00`).getTime();
  const end = new Date(`${endDate}T00:00:00`).getTime();
  const interval = Math.max((end - start) / 5, 0);

  return [240, 285, 320, 390, 430, 482.9].map((value, index) => ({
    period: new Date(start + interval * index).toISOString(),
    value,
  }));
}
